import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  CircularProgress,
  Typography,
  Chip,
  TextField,
  Paper,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from './AuthContext';
import AchievementModal from './AchievementModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function AchievementCarousel({ refreshTrigger, isTrap = false, onClaim }) {
  const [allAchievements, setAllAchievements] = useState([]);
  const [claimedAchievements, setClaimedAchievements] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hovering, setHovering] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingParScore, setEditingParScore] = useState(false);
  const [hoveringImage, setHoveringImage] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedParScore, setEditedParScore] = useState(0);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const { user, isAuthenticated, adminDisplayEnabled } = useAuth();
  const isAdmin = adminDisplayEnabled;
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const fetchAchievements = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/achievements`);
      
      // Filter by is_trap if specified
      let filtered = response.data;
      if (isTrap) {
        filtered = response.data.filter(a => a.is_trap === true);
      } else {
        filtered = response.data.filter(a => !a.is_trap || a.is_trap === false);
      }
      
      // Sort by par_score
      // Handicaps: ascending (lower scores first)
      // Traps: descending (higher scores first)
      const sorted = filtered.sort((a, b) => {
        if (a.par_score !== b.par_score) {
          if (isTrap) {
            // Traps: descending order (higher par_score first)
            return b.par_score - a.par_score;
          } else {
            // Handicaps: ascending order (lower par_score first)
            return a.par_score - b.par_score;
          }
        }
        // Secondary sort by id to maintain stable order for same par_score
        return a.id.localeCompare(b.id);
      });
      setAllAchievements(sorted);
      
      // Fetch user's claimed achievements if logged in
      if (user && (user.id || user._id)) {
        const userId = user.id || user._id;
        try {
          const claimedResponse = await axios.get(`${API_URL}/api/users/${userId}/achievements`);
          const claimedIds = new Set(claimedResponse.data.map(a => a.id));
          setClaimedAchievements(claimedIds);
        } catch (error) {
          console.error('Error fetching claimed achievements:', error);
          setClaimedAchievements(new Set());
        }
      } else {
        setClaimedAchievements(new Set());
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching achievements:', error);
      setError('Failed to load achievements');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAchievements();
    setCurrentIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, isTrap]);
  
  // Separate effect to update claimed status when user changes
  useEffect(() => {
    if (user && (user.id || user._id) && allAchievements.length > 0) {
      const userId = user.id || user._id;
      axios.get(`${API_URL}/api/users/${userId}/achievements`)
        .then(response => {
          const claimedIds = new Set(response.data.map(a => a.id));
          setClaimedAchievements(claimedIds);
        })
        .catch(error => {
          console.error('Error fetching claimed achievements:', error);
          setClaimedAchievements(new Set());
        });
    } else if (!user) {
      setClaimedAchievements(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?._id, allAchievements.length]);

  // Update edited values when current achievement changes
  useEffect(() => {
    if (allAchievements.length > 0 && currentIndex < allAchievements.length) {
      const achievement = allAchievements[currentIndex];
      setEditedName(achievement.name || '');
      setEditedDescription(achievement.description || '');
      setEditedParScore(achievement.par_score || 0);
      setImageUrl(achievement.image_url || '');
      setEditingName(false);
      setEditingDescription(false);
      setEditingParScore(false);
    }
  }, [currentIndex, allAchievements]);

  const checkClaimed = async (achievement) => {
    if (!user || !achievement) return false;
    const userId = user.id || user._id;
    if (!userId) return false;
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}/achievements`);
      return response.data.some(a => a.id === achievement.id);
    } catch (error) {
      console.error('Error checking claimed status:', error);
      return false;
    }
  };

  const handleClaim = async (achievement) => {
    if (!isAuthenticated || !user) {
      alert('Please sign in to claim achievements');
      return;
    }

    const userId = user.id || user._id;
    if (!userId) {
      alert('User ID not found');
      return;
    }

    const isClaimed = await checkClaimed(achievement);
    setClaiming(true);
    try {
      if (isClaimed) {
        await axios.delete(`${API_URL}/api/users/${userId}/achievements/${achievement.id}`);
        setClaimedAchievements(prev => {
          const newSet = new Set(prev);
          newSet.delete(achievement.id);
          return newSet;
        });
      } else {
        await axios.post(`${API_URL}/api/users/${userId}/achievements/${achievement.id}`);
        setClaimedAchievements(prev => new Set([...prev, achievement.id]));
      }
      fetchAchievements();
      
      // Trigger rankings update if callback provided
      if (onClaim) {
        onClaim();
      }
    } catch (error) {
      console.error('Error claiming achievement:', error);
      alert('Failed to claim achievement. Please try again.');
    } finally {
      setClaiming(false);
    }
  };

  const handleSaveField = async (field, value, achievement) => {
    if (!isAdmin || !achievement) return;
    
    try {
      const updateData = {
        name: field === 'name' ? value : editedName,
        description: field === 'description' ? value : editedDescription,
        par_score: field === 'par_score' ? parseInt(value) || 0 : editedParScore,
        image_url: achievement.image_url,
        is_trap: achievement.is_trap || false,
      };
      
      await axios.put(`${API_URL}/api/achievements/${achievement.id}`, updateData);
      fetchAchievements();
    } catch (error) {
      console.error('Error updating achievement:', error);
      alert('Failed to update achievement. Please try again.');
    }
  };

  const handleImageChange = async (e, achievement) => {
    console.log('handleImageChange called', { isAdmin, achievement, files: e.target.files });
    
    if (!isAdmin || !achievement) {
      console.log('Early return: not admin or no achievement');
      return;
    }
    
    const file = e.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }
    
    console.log('File selected:', file.name, file.type, file.size);
    
    setUploadingImage(true);
    setUploadSuccess(false);
    
    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageUrl(reader.result);
    };
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
    };
    reader.readAsDataURL(file);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log('Uploading file to:', `${API_URL}/api/upload`);
      const uploadResponse = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Upload response:', uploadResponse.data);
      
      const updateData = {
        name: editedName,
        description: editedDescription,
        par_score: editedParScore,
        image_url: uploadResponse.data.url,
        is_trap: achievement.is_trap || false,
      };
      
      console.log('Updating achievement:', achievement.id, updateData);
      await axios.put(`${API_URL}/api/achievements/${achievement.id}`, updateData);
      setImageUrl(uploadResponse.data.url);
      setUploadSuccess(true);
      fetchAchievements();
      
      // Clear success message after 2 seconds
      setTimeout(() => {
        setUploadSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error updating image:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert(`Failed to update image: ${error.response?.data?.detail || error.message}`);
      setImageUrl(achievement.image_url || '');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  // Calculate total items
  const totalItems = allAchievements.length;
  
  const handlePrevious = () => {
    if (totalItems === 0) return;
    setCurrentIndex(prev => {
      // Loop to last item if at first
      return prev === 0 ? totalItems - 1 : prev - 1;
    });
  };

  const handleNext = () => {
    if (totalItems === 0) return;
    setCurrentIndex(prev => {
      // Loop to first item if at last
      return prev === totalItems - 1 ? 0 : prev + 1;
    });
  };

  // Swipe gesture handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrevious();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (allAchievements.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" color="text.secondary">
          No achievements yet. Check back soon!
        </Typography>
      </Box>
    );
  }

  // Get previous, current, and next items for peek effect
  const getItemAtIndex = (index) => {
    if (index < 0) {
      return allAchievements[totalItems + index];
    }
    if (index >= totalItems) {
      return allAchievements[index - totalItems];
    }
    return allAchievements[index];
  };

  const prevIndex = currentIndex === 0 ? totalItems - 1 : currentIndex - 1;
  const nextIndex = currentIndex === totalItems - 1 ? 0 : currentIndex + 1;
  const prevItem = getItemAtIndex(prevIndex);
  const currentItem = allAchievements[currentIndex];
  const nextItem = getItemAtIndex(nextIndex);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 },
          touchAction: 'pan-y',
          overflow: 'hidden',
          px: { xs: 0.5, sm: 2, md: 4 },
        }}
      >
        {/* Left Arrow */}
        {totalItems > 1 && (
          <IconButton
            onClick={handlePrevious}
            size="small"
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              padding: { xs: '6px', sm: '8px' },
              flexShrink: 0,
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
          </IconButton>
        )}

        {/* Achievement Items with Peek Effect */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 0.5, sm: 1 },
            flex: 1,
            minWidth: 0,
            position: 'relative',
            overflow: 'visible',
            minHeight: { xs: 280, sm: 240, md: 200 },
          }}
        >
          {/* Previous Item (peek) */}
          {totalItems > 1 && (
            <Box
              sx={{
                flex: '0 0 25%',
                maxWidth: '25%',
                opacity: 0.6,
                transform: 'scale(0.85)',
                transition: 'all 0.3s ease',
                pointerEvents: 'none',
                overflow: 'hidden',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  width: '100%',
                  minHeight: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  component="img"
                  src={prevItem.image_url}
                  alt={prevItem.name}
                  sx={{
                    width: 80,
                    height: 80,
                    mb: 1,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    objectFit: 'cover',
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  {prevItem.name}
                </Typography>
                <Chip
                  label={`Par: ${prevItem.par_score}`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600, fontSize: '0.65rem' }}
                />
              </Paper>
            </Box>
          )}

          {/* Current Item (full) */}
          <Box
            sx={{
              flex: { xs: '0 0 100%', sm: '0 0 60%', md: '0 0 40%' },
              maxWidth: { xs: '100%', sm: '60%', md: '400px' },
              transition: 'all 0.3s ease',
              zIndex: 1,
            }}
          >
            <Paper
              elevation={2}
              sx={{
                p: { xs: 1.5, sm: 2, md: 2.4 },
                width: '100%',
                minHeight: { xs: 280, sm: 240, md: 200 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box 
                sx={{ position: 'relative', width: '100%', maxWidth: 400 }}
                onMouseEnter={() => isAdmin && setHoveringImage(true)}
                onMouseLeave={() => setHoveringImage(false)}
              >
                <Box
                  component="img"
                  src={imageUrl || currentItem.image_url}
                  alt={currentItem.name}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    mb: 0.5,
                    border: '2px solid',
                    borderColor: uploadSuccess ? '#4caf50' : 'primary.main',
                    borderRadius: 2,
                    objectFit: 'cover',
                    flexShrink: 0,
                    display: 'block',
                    opacity: uploadingImage ? 0.6 : 1,
                    transition: 'all 0.3s ease',
                  }}
                />
                {uploadingImage && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={40} sx={{ color: 'white' }} />
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
                      Uploading...
                    </Typography>
                  </Box>
                )}
                {uploadSuccess && !uploadingImage && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 3,
                      backgroundColor: '#4caf50',
                      color: 'white',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    ✓ Saved
                  </Box>
                )}
                {isAdmin && hoveringImage && !uploadingImage && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 2,
                    }}
                  >
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('Opening edit modal for:', currentItem);
                        setSelectedAchievement(currentItem);
                        setModalOpen(true);
                      }}
                      sx={{
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        },
                      }}
                      title="Edit Achievement"
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
              {isAdmin && editingName ? (
                <TextField
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onBlur={() => {
                    setEditingName(false);
                    handleSaveField('name', editedName, currentItem);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditingName(false);
                      handleSaveField('name', editedName, currentItem);
                    }
                    if (e.key === 'Escape') {
                      setEditingName(false);
                      setEditedName(currentItem.name);
                    }
                  }}
                  autoFocus
                  variant="standard"
                  sx={{
                    mt: 0.5,
                    mb: 0.5,
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                      fontSize: '1.5rem',
                    },
                  }}
                />
              ) : (
                <Typography 
                  variant="h5" 
                  onClick={() => isAdmin && setEditingName(true)}
                  sx={{ 
                    fontWeight: 600, 
                    mb: 0.5,
                    mt: 0.5,
                    textTransform: 'uppercase',
                    cursor: isAdmin ? 'pointer' : 'default',
                    '&:hover': isAdmin ? {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 1,
                    } : {},
                  }}
                >
                  {currentItem.name}
                </Typography>
              )}
              {isAdmin && editingDescription ? (
                <TextField
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  onBlur={() => {
                    setEditingDescription(false);
                    handleSaveField('description', editedDescription, currentItem);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditingDescription(false);
                      handleSaveField('description', editedDescription, currentItem);
                    }
                    if (e.key === 'Escape') {
                      setEditingDescription(false);
                      setEditedDescription(currentItem.description);
                    }
                  }}
                  autoFocus
                  multiline
                  variant="standard"
                  sx={{
                    mb: 0.5,
                    px: 1,
                    width: '100%',
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                    },
                  }}
                />
              ) : (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  onClick={() => isAdmin && setEditingDescription(true)}
                  sx={{ 
                    mb: 0.5,
                    px: 1,
                    cursor: isAdmin ? 'pointer' : 'default',
                    '&:hover': isAdmin ? {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 1,
                    } : {},
                  }}
                >
                  {currentItem.description}
                </Typography>
              )}
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mt: 'auto', mb: 0.6 }}
              >
                {claimedAchievements.has(currentItem.id) && isAuthenticated ? (
                  <>
                    <Chip
                      label={`${currentItem.par_score >= 0 ? '+' : ''}${currentItem.par_score} points`}
                      size="small"
                      sx={{
                        backgroundColor: currentItem.par_score < 0 ? '#4caf50' : '#667eea',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                      }}
                    />
                    <Chip
                      label="Unclaim"
                      onClick={() => handleClaim(currentItem)}
                      disabled={claiming}
                      size="small"
                      sx={{
                        backgroundColor: '#f44336',
                        color: 'white',
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#d32f2f',
                        },
                      }}
                    />
                  </>
                ) : (isAuthenticated && !claimedAchievements.has(currentItem.id)) || (isAdmin && !claimedAchievements.has(currentItem.id)) ? (
                  <>
                    {isAdmin && editingParScore ? (
                      <TextField
                        type="number"
                        value={editedParScore}
                        onChange={(e) => setEditedParScore(parseInt(e.target.value) || 0)}
                        onBlur={() => {
                          setEditingParScore(false);
                          handleSaveField('par_score', editedParScore, currentItem);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setEditingParScore(false);
                            handleSaveField('par_score', editedParScore, currentItem);
                          }
                          if (e.key === 'Escape') {
                            setEditingParScore(false);
                            setEditedParScore(currentItem.par_score);
                          }
                        }}
                        autoFocus
                        variant="standard"
                        inputProps={{ min: -999, max: 999 }}
                        sx={{
                          width: 80,
                          '& .MuiInputBase-input': {
                            textAlign: 'center',
                            fontWeight: 600,
                          },
                        }}
                      />
                    ) : (
                      <Chip
                        label={`Par: ${currentItem.par_score}`}
                        onClick={() => isAdmin && setEditingParScore(true)}
                        size="small"
                        color="primary"
                        sx={{ 
                          fontWeight: 600,
                          cursor: isAdmin ? 'pointer' : 'default',
                        }}
                      />
                    )}
                    <Chip
                      label="Claim"
                      onClick={() => handleClaim(currentItem)}
                      disabled={claiming}
                      size="small"
                      sx={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        fontWeight: 600,
                        cursor: 'pointer',
                        px: '5px',
                        '&:hover': {
                          backgroundColor: '#45a049',
                        },
                      }}
                    />
                  </>
                ) : (
                  isAdmin && editingParScore ? (
                    <TextField
                      type="number"
                      value={editedParScore}
                      onChange={(e) => setEditedParScore(parseInt(e.target.value) || 0)}
                      onBlur={() => {
                        setEditingParScore(false);
                        handleSaveField('par_score', editedParScore, currentItem);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setEditingParScore(false);
                          handleSaveField('par_score', editedParScore, currentItem);
                        }
                        if (e.key === 'Escape') {
                          setEditingParScore(false);
                          setEditedParScore(currentItem.par_score);
                        }
                      }}
                      autoFocus
                      variant="standard"
                      inputProps={{ min: -999, max: 999 }}
                      sx={{
                        width: 80,
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          fontWeight: 600,
                        },
                      }}
                    />
                  ) : (
                    <Chip
                      label={`Par: ${currentItem.par_score}`}
                      onClick={() => isAdmin && setEditingParScore(true)}
                      size="small"
                      color="primary"
                      sx={{ 
                        fontWeight: 600,
                        cursor: isAdmin ? 'pointer' : 'default',
                      }}
                    />
                  )
                )}
              </Box>
            </Paper>
          </Box>

          {/* Next Item (peek) */}
          {totalItems > 1 && (
            <Box
              sx={{
                flex: '0 0 25%',
                maxWidth: '25%',
                opacity: 0.6,
                transform: 'scale(0.85)',
                transition: 'all 0.3s ease',
                pointerEvents: 'none',
                overflow: 'hidden',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  width: '100%',
                  minHeight: 200,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  component="img"
                  src={nextItem.image_url}
                  alt={nextItem.name}
                  sx={{
                    width: 80,
                    height: 80,
                    mb: 1,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    objectFit: 'cover',
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  {nextItem.name}
                </Typography>
                <Chip
                  label={`Par: ${nextItem.par_score}`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600, fontSize: '0.65rem' }}
                />
              </Paper>
            </Box>
          )}
        </Box>

        {/* Right Arrow */}
        {totalItems > 1 && (
          <IconButton
            onClick={handleNext}
            size="small"
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              padding: { xs: '6px', sm: '8px' },
              flexShrink: 0,
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: { xs: '20px', sm: '24px' } }} />
          </IconButton>
        )}
      </Box>


      <AchievementModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedAchievement(null);
        }}
        achievement={selectedAchievement}
        onClaim={() => {
          fetchAchievements();
        }}
        onUpdate={() => {
          fetchAchievements();
        }}
      />
    </Box>
  );
}

export default AchievementCarousel;

